/**
 * N226 - sincronizacoes que o app reportou, com o total dividido em servidor
 * (uniao das requisicoes com o mesmo sync_id) x rede/app, e o detalhe por
 * etapa e por requisicao. Usuario, maquina e versao filtram com um clique
 * (maquina e versao por trecho, como o backend).
 */
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, X, XCircle } from 'lucide-react';
import { formatBytes } from '../../services/diagnosticoService';
import {
  SEM_DADOS,
  SEM_VALOR,
  SincronizacaoDetalhe,
  SincronizacaoItem,
  SincronizacaoListResponse,
  formatarDataHora,
  formatarMs,
  formatarMsSeHouver,
  listarSincronizacoes,
  mensagemDeErro,
  obterSincronizacao,
} from '../../services/diagnosticoNuvemService';
import { formatarQuantidade } from '../../utils/formatters';
import { MESSAGES } from '../../utils/messages';
import { Tabela } from './Tabela';

const PAGE_SIZE = 20;
const COLUNAS = [
  '', 'Início', 'Usuário', 'Máquina', 'Versão', 'Gatilho', 'Total', 'Servidor', 'Rede/app',
  'Req. app/servidor', 'Etapa mais lenta', 'Status',
];
const TD = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap';

// so `nome` e `ms` sao garantidos na etapa: campo que o app nao mandou e '—', nao o '-' do formatter
const qtd = (n?: number) => (n == null ? SEM_VALOR : formatarQuantidade(n));

interface Filtros {
  usuario?: { id: number; nome: string };
  device_name?: string;
  app_version?: string;
}

const Filtro = ({ texto, titulo, onClick }: { texto: string; titulo: string; onClick: () => void }) =>
  texto ? (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      className="text-left text-blue-700 dark:text-blue-400 hover:underline"
    >
      {texto}
    </button>
  ) : (
    <>{SEM_VALOR}</>
  );

const DetalheSync = ({ item, detalhe }: { item: SincronizacaoItem; detalhe?: SincronizacaoDetalhe | string }) => {
  if (detalhe === undefined) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{MESSAGES.GENERIC.LOADING}</p>;
  }
  if (typeof detalhe === 'string') {
    return <p className="text-sm text-red-700 dark:text-red-400">{detalhe}</p>;
  }
  // a etapa que mais pesa primeiro: e nela que se mexe
  const etapas = [...detalhe.etapas].sort((a, b) => (b.ms ?? 0) - (a.ms ?? 0));
  const fatia = (ms?: number) =>
    item.duracao_ms > 0 && ms != null ? `${Math.round((ms / item.duracao_ms) * 100)}%` : SEM_VALOR;
  const rota = (r: string) => <span className="font-mono text-xs">{r}</span>;

  return (
    <div className="space-y-4">
      {item.erro && (
        <p className="text-sm text-red-700 dark:text-red-400 whitespace-pre-wrap">Erro: {item.erro}</p>
      )}
      <Tabela
        titulo="Etapas (mais lenta primeiro)"
        cabecalho={['Etapa', 'Tempo', '% do total', 'Requisições', 'Itens', 'Retentativas', 'Erros']}
        linhas={etapas.map((e) => [
          e.nome,
          formatarMs(e.ms),
          fatia(e.ms),
          qtd(e.requisicoes),
          qtd(e.itens),
          qtd(e.retentativas),
          qtd(e.erros),
        ])}
      />
      <Tabela
        titulo="Rotas chamadas"
        cabecalho={['Método', 'Rota', 'Requisições', 'Total', 'p50', 'Máx']}
        linhas={detalhe.rotas.map((r) => [
          r.metodo, rota(r.rota), formatarQuantidade(r.n), formatarMs(r.total_ms), formatarMs(r.p50_ms), formatarMs(r.max_ms),
        ])}
      />
      <Tabela
        titulo={`Requisições no servidor (${formatarQuantidade(detalhe.requisicoes.length)})`}
        rolagem
        rodape={detalhe.truncado ? 'Mostrando só as primeiras 2.000 requisições.' : undefined}
        cabecalho={['Hora', 'Método', 'Rota', 'Status', 'Duração', 'Banco (consultas)', 'CPU', 'Bytes']}
        linhas={detalhe.requisicoes.map((r) => [
          formatarDataHora(r.criado_em),
          r.metodo,
          rota(r.rota),
          <span className={r.status >= 400 ? 'font-semibold text-red-700 dark:text-red-400' : ''}>{r.status}</span>,
          formatarMs(r.duracao_ms),
          `${formatarMs(r.db_ms)} (${r.db_consultas})`,
          formatarMs(r.cpu_ms),
          formatBytes(r.bytes_resposta),
        ])}
      />
    </div>
  );
};

interface SincronizacoesTabelaProps {
  inicio: string;
  fim: string;
  /** false: nao busca nem mostra linhas, mas guarda filtros e pagina para quando corrigir */
  periodoValido: boolean;
  /** muda a cada "Atualizar" da pagina */
  recarga: number;
}

export const SincronizacoesTabela = ({ inicio, fim, periodoValido, recarga }: SincronizacoesTabelaProps) => {
  const [filtros, setFiltros] = useState<Filtros>({});
  const [page, setPage] = useState(1);
  const [lista, setLista] = useState<SincronizacaoListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  // do detalhe aberto: undefined = carregando; texto = erro
  const [detalhe, setDetalhe] = useState<SincronizacaoDetalhe | string>();
  const ultimaCarga = useRef(0);

  // periodo novo comeca da pagina 1 (a carga com a pagina velha e descartada abaixo).
  // O invalido nao conta: corrigido de volta para o mesmo periodo, a pagina continua.
  const periodo = `${inicio}..${fim}`;
  const ultimoPeriodoValido = useRef(periodo);
  useEffect(() => {
    if (!periodoValido || periodo === ultimoPeriodoValido.current) return;
    ultimoPeriodoValido.current = periodo;
    setPage(1);
  }, [periodo, periodoValido]);

  useEffect(() => {
    // resposta que chega depois de outra mais nova nao sobrescreve a tela
    const minha = ++ultimaCarga.current;
    // periodo que o backend recusaria nem vai a API (e a carga em voo e descartada)
    if (!periodoValido) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    listarSincronizacoes({
      inicio,
      fim,
      page,
      page_size: PAGE_SIZE,
      usuario_id: filtros.usuario?.id,
      device_name: filtros.device_name,
      app_version: filtros.app_version,
    })
      .then((r) => {
        if (minha === ultimaCarga.current) setLista(r);
      })
      .catch((e) => {
        if (minha !== ultimaCarga.current) return;
        setLista(null);
        setError(mensagemDeErro(e));
      })
      .finally(() => {
        if (minha === ultimaCarga.current) setLoading(false);
      });
  }, [inicio, fim, periodoValido, page, filtros, recarga]);

  const filtrar = (f: Filtros) => {
    setFiltros((atual) => ({ ...atual, ...f }));
    setPage(1);
  };

  // Sem cache: buscado ao abrir e a cada Atualizar. Com cache, o erro de antes ficava
  // na tela ao reabrir e o Atualizar mostrava o detalhe velho. A resposta de uma
  // abertura anterior (ou de antes do Atualizar) e descartada.
  useEffect(() => {
    if (!aberta) return;
    let valendo = true;
    setDetalhe(undefined);
    obterSincronizacao(aberta)
      .then((d) => {
        if (valendo) setDetalhe(d);
      })
      .catch((e) => {
        if (valendo) setDetalhe(mensagemDeErro(e));
      });
    return () => {
      valendo = false;
    };
  }, [aberta, recarga]);

  // limpa no mesmo render: abrir B nunca mostra, nem por um quadro, o detalhe de A
  const alternar = (syncId: string) => {
    setDetalhe(undefined);
    setAberta((a) => (a === syncId ? null : syncId));
  };

  // maquina e versao o backend filtra por trecho (icontains): o chip tem de dizer
  const chips: [keyof Filtros, string][] = [];
  if (filtros.usuario) chips.push(['usuario', `Usuário: ${filtros.usuario.nome}`]);
  if (filtros.device_name) chips.push(['device_name', `Máquina contém: ${filtros.device_name}`]);
  if (filtros.app_version) chips.push(['app_version', `Versão contém: ${filtros.app_version}`]);

  // periodo invalido: nada do periodo anterior aparece (linhas, total, erro); filtros e pagina ficam
  const exibida = periodoValido ? lista : null;
  const totalPages = exibida?.total_pages || 1;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Sincronizações do app</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Servidor = tempo com alguma requisição desta sincronização em atendimento; rede/app = o
            resto (rede, gravação no aparelho e a fila do servidor quando ele está saturado — a espera
            por um dos lugares do gunicorn acontece antes da medição). Clique no usuário, na máquina ou na versão
            para filtrar.
          </p>
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {chips.map(([campo, rotulo]) => (
              <button
                key={campo}
                type="button"
                onClick={() => filtrar({ [campo]: undefined })}
                aria-label={`Remover filtro ${rotulo}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
              >
                {rotulo}
                <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      {periodoValido && error && (
        <div className="m-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className={`overflow-x-auto ${loading && exibida ? 'opacity-60' : ''}`}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              {COLUNAS.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {!exibida && loading && (
              <tr>
                <td colSpan={COLUNAS.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {MESSAGES.GENERIC.LOADING}
                </td>
              </tr>
            )}
            {exibida && exibida.items.length === 0 && (
              <tr>
                <td colSpan={COLUNAS.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  {SEM_DADOS}
                </td>
              </tr>
            )}
            {exibida?.items.map((s) => {
              const nome = s.usuario_nome || `#${s.usuario_id}`;
              return (
                <Fragment key={s.sync_id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 tabular-nums">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => alternar(s.sync_id)}
                        aria-expanded={aberta === s.sync_id}
                        aria-label={`Detalhe da sincronização ${s.sync_id}`}
                        className="p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        {aberta === s.sync_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td className={TD}>{formatarDataHora(s.iniciado_em)}</td>
                    <td className={TD}>
                      {s.usuario_id !== null ? (
                        <Filtro
                          texto={nome}
                          titulo="Filtrar por este usuário"
                          onClick={() => filtrar({ usuario: { id: s.usuario_id, nome } })}
                        />
                      ) : (
                        SEM_VALOR
                      )}
                    </td>
                    <td className={TD}>
                      <Filtro
                        texto={s.device_name}
                        titulo="Filtrar por esta máquina"
                        onClick={() => filtrar({ device_name: s.device_name })}
                      />
                      {s.plataforma && (
                        <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({s.plataforma})</span>
                      )}
                    </td>
                    <td className={TD}>
                      <Filtro
                        texto={s.app_version}
                        titulo="Filtrar por esta versão"
                        onClick={() => filtrar({ app_version: s.app_version })}
                      />
                    </td>
                    <td className={TD}>{[s.gatilho, s.tipo].filter(Boolean).join(' · ') || SEM_VALOR}</td>
                    <td className={`${TD} font-semibold`}>{formatarMs(s.duracao_ms)}</td>
                    {/* nenhuma requisicao com este sync_id (servidor antigo, app sem X-Sync-Id):
                        servidor nao e "0 ms" e rede/app nao e o total inteiro */}
                    <td className={TD}>{formatarMsSeHouver(s.servidor_ms, s.requisicoes_servidor)}</td>
                    <td className={TD}>{formatarMsSeHouver(s.rede_app_ms, s.requisicoes_servidor)}</td>
                    <td className={TD}>
                      {formatarQuantidade(s.requisicoes)} / {formatarQuantidade(s.requisicoes_servidor)}
                      {s.retentativas > 0 && (
                        <span className="ml-1 text-xs text-amber-700 dark:text-amber-400">
                          ({s.retentativas} retent.)
                        </span>
                      )}
                    </td>
                    <td className={TD}>
                      {s.etapa_mais_lenta
                        ? `${s.etapa_mais_lenta.nome} (${formatarMs(s.etapa_mais_lenta.ms)})`
                        : SEM_VALOR}
                    </td>
                    <td className={TD}>
                      {s.sucesso ? (
                        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                          <CheckCircle2 size={14} /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400" title={s.erro}>
                          <XCircle size={14} /> Erro
                        </span>
                      )}
                    </td>
                  </tr>
                  {aberta === s.sync_id && (
                    <tr>
                      <td colSpan={COLUNAS.length} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/40">
                        <DetalheSync item={s} detalhe={detalhe} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between p-4 text-sm text-gray-600 dark:text-gray-400">
          <span>
            Página {page} de {totalPages} — {exibida?.total ?? 0} registros
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
